<template>
  <div v-if="isOpen" class="modal-backdrop" @click.self="$emit('close')">
    <div :class="['modal-container', darkMode ? 'dark-theme' : 'light-theme']">
      <!-- Header -->
      <div class="modal-header">
        <div class="flex items-center gap-3">
          <i class="fas fa-globe text-green-500 text-xl"></i>
          <h2 class="text-xl font-bold">MealWave Online Orders</h2>
        </div>
        <button @click="$emit('close')" class="modal-close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Connection Status -->
        <div v-if="connectionStatus === 'approved'" class="status-card approved">
          <i class="fas fa-check-circle text-3xl"></i>
          <div>
            <h3 class="font-bold text-lg">Connected to MealWave</h3>
            <p class="text-sm opacity-80">Restaurant ID: {{ connectedMealWaveId }}</p>
            <p class="text-sm opacity-80">Online orders will appear automatically</p>
          </div>
          <button @click="disconnectMealWave" class="disconnect-btn">
            <i class="fas fa-unlink mr-2"></i>Disconnect
          </button>
        </div>

        <div v-else-if="connectionStatus === 'pending'" class="status-card pending">
          <i class="fas fa-clock text-3xl"></i>
          <div>
            <h3 class="font-bold text-lg">Request Pending</h3>
            <p class="text-sm opacity-80">Waiting for approval from MealWave admin</p>
            <p class="text-sm opacity-80">Restaurant ID: {{ pendingMealWaveId }}</p>
          </div>
          <button @click="cancelRequest" class="cancel-btn">
            <i class="fas fa-times mr-2"></i>Cancel Request
          </button>
        </div>

        <div v-else-if="connectionStatus === 'rejected'" class="status-card rejected">
          <i class="fas fa-times-circle text-3xl"></i>
          <div>
            <h3 class="font-bold text-lg">Request Rejected</h3>
            <p class="text-sm opacity-80">Your connection request was not approved</p>
            <p class="text-sm opacity-80">Restaurant ID: {{ rejectedMealWaveId }}</p>
          </div>
          <button @click="clearRejection" class="retry-btn">
            <i class="fas fa-redo mr-2"></i>Try Again
          </button>
        </div>

        <!-- Request Form (when not connected) -->
        <div v-else class="request-form">
          <div class="info-box">
            <i class="fas fa-info-circle text-blue-500 text-xl"></i>
            <p>Connect your Table Tech system to receive online orders from MealWave. Enter your MealWave Restaurant ID below.</p>
          </div>

          <div class="form-group">
            <label for="mealwave-id">MealWave Restaurant ID</label>
            <input
              id="mealwave-id"
              v-model="mealWaveRestaurantId"
              type="text"
              placeholder="e.g., restaurant-name-1234567890"
              :class="['form-input', { 'error': inputError }]"
            />
            <p v-if="inputError" class="error-text">{{ inputError }}</p>
          </div>

          <button
            @click="sendConnectionRequest"
            :disabled="isLoading || !mealWaveRestaurantId.trim()"
            class="submit-btn"
          >
            <i v-if="isLoading" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else class="fas fa-paper-plane mr-2"></i>
            {{ isLoading ? 'Sending...' : 'Send Connection Request' }}
          </button>
        </div>

        <!-- Help Section -->
        <div class="help-section">
          <h4 class="font-bold mb-2"><i class="fas fa-question-circle mr-2"></i>How to find your MealWave Restaurant ID?</h4>
          <ol class="text-sm opacity-80 list-decimal list-inside space-y-1">
            <li>Log in to MealWave Admin Panel</li>
            <li>Go to Settings → Restaurant Info</li>
            <li>Copy the Restaurant ID shown there</li>
          </ol>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'MealWaveConnectionModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mealWaveRestaurantId: '',
      isLoading: false,
      inputError: '',
      connectionStatus: null, // null, 'pending', 'approved', 'rejected'
      connectedMealWaveId: '',
      pendingMealWaveId: '',
      rejectedMealWaveId: '',
      connectionListener: null
    };
  },
  computed: {
    ...mapState(['selectedRestaurantId', 'selectedRestaurantName', 'darkMode'])
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.checkExistingConnection();
      }
    }
  },
  mounted() {
    if (this.isOpen) {
      this.checkExistingConnection();
    }
  },
  beforeDestroy() {
    if (this.connectionListener) {
      this.connectionListener();
    }
  },
  methods: {
    async checkExistingConnection() {
      try {
        const { ref, onValue } = await import('firebase/database');
        const mealwaveDb = this.$firebase.mealwaveDatabase();

        // Listen to all connections for this Table Tech restaurant
        const connectionsRef = ref(mealwaveDb, `mealwave_connections`);

        this.connectionListener = onValue(connectionsRef, (snapshot) => {
          const allConnections = snapshot.val() || {};

          // Search for any connection involving this Table Tech restaurant
          for (const [mealwaveId, connections] of Object.entries(allConnections)) {
            if (connections[this.selectedRestaurantId]) {
              const connection = connections[this.selectedRestaurantId];
              this.connectionStatus = connection.status;

              if (connection.status === 'approved') {
                this.connectedMealWaveId = mealwaveId;
              } else if (connection.status === 'pending') {
                this.pendingMealWaveId = mealwaveId;
              } else if (connection.status === 'rejected') {
                this.rejectedMealWaveId = mealwaveId;
              }
              return;
            }
          }

          // No connection found
          this.connectionStatus = null;
        });
      } catch (error) {
        console.error('Error checking MealWave connection:', error);
      }
    },

    async sendConnectionRequest() {
      if (!this.mealWaveRestaurantId.trim()) {
        this.inputError = 'Please enter a MealWave Restaurant ID';
        return;
      }

      this.isLoading = true;
      this.inputError = '';

      try {
        const { ref, set, get } = await import('firebase/database');
        const mealwaveDb = this.$firebase.mealwaveDatabase();

        // First check if the MealWave restaurant exists
        const restaurantRef = ref(mealwaveDb, `restaurants/${this.mealWaveRestaurantId}`);
        const restaurantSnap = await get(restaurantRef);

        if (!restaurantSnap.exists()) {
          this.inputError = 'MealWave Restaurant not found. Please check the ID.';
          this.isLoading = false;
          return;
        }

        // Create connection request
        const connectionRef = ref(mealwaveDb, `mealwave_connections/${this.mealWaveRestaurantId}/${this.selectedRestaurantId}`);

        await set(connectionRef, {
          status: 'pending',
          requestedAt: new Date().toISOString(),
          tableTechRestaurantId: this.selectedRestaurantId,
          tableTechRestaurantName: this.selectedRestaurantName || this.selectedRestaurantId
        });

        console.log('📱 MealWave connection request sent');
        this.pendingMealWaveId = this.mealWaveRestaurantId;
        this.connectionStatus = 'pending';
        this.mealWaveRestaurantId = '';

      } catch (error) {
        console.error('Error sending connection request:', error);
        this.inputError = 'Failed to send request. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async cancelRequest() {
      try {
        const { ref, remove } = await import('firebase/database');
        const mealwaveDb = this.$firebase.mealwaveDatabase();

        const connectionRef = ref(mealwaveDb, `mealwave_connections/${this.pendingMealWaveId}/${this.selectedRestaurantId}`);
        await remove(connectionRef);

        this.connectionStatus = null;
        this.pendingMealWaveId = '';
        console.log('📱 MealWave connection request cancelled');

      } catch (error) {
        console.error('Error cancelling request:', error);
      }
    },

    async disconnectMealWave() {
      if (!confirm('Are you sure you want to disconnect from MealWave? You will stop receiving online orders.')) {
        return;
      }

      try {
        const { ref, remove } = await import('firebase/database');
        const mealwaveDb = this.$firebase.mealwaveDatabase();

        const connectionRef = ref(mealwaveDb, `mealwave_connections/${this.connectedMealWaveId}/${this.selectedRestaurantId}`);
        await remove(connectionRef);

        this.connectionStatus = null;
        this.connectedMealWaveId = '';
        console.log('📱 Disconnected from MealWave');

        // Emit event to stop listening for MealWave orders
        this.$emit('disconnected');

      } catch (error) {
        console.error('Error disconnecting from MealWave:', error);
      }
    },

    clearRejection() {
      this.connectionStatus = null;
      this.rejectedMealWaveId = '';
    }
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-container {
  border-radius: var(--ods-radius);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.dark-theme {
  background: var(--ods-surface);
  color: var(--ods-ink);
}

.light-theme {
  background: var(--ods-surface);
  color: var(--ods-ink);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.2);
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  color: inherit;
  opacity: 0.7;
}

.modal-close-btn:hover {
  opacity: 1;
  background: rgba(128, 128, 128, 0.2);
}

.modal-body {
  padding: 20px;
  max-height: calc(90vh - 80px);
  overflow-y: auto;
}

/* Status Cards */
.status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px;
  border-radius: var(--ods-radius);
  margin-bottom: 20px;
  gap: 16px;
}

.status-card.approved {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(16, 185, 129, 0.1));
  border: 2px solid #10b981;
  color: #10b981;
}

.status-card.pending {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(245, 158, 11, 0.1));
  border: 2px solid #f59e0b;
  color: #f59e0b;
}

.status-card.rejected {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.1));
  border: 2px solid #ef4444;
  color: #ef4444;
}

.disconnect-btn, .cancel-btn, .retry-btn {
  padding: 10px 20px;
  border-radius: var(--ods-radius);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.disconnect-btn {
  background: #ef4444;
  color: var(--ods-ink);
}

.disconnect-btn:hover {
  background: #dc2626;
}

.cancel-btn {
  background: var(--ods-ink-3);
  color: var(--ods-ink);
}

.cancel-btn:hover {
  background: var(--ods-surface-2);
}

.retry-btn {
  background: var(--ods-accent);
  color: var(--ods-ink);
}

.retry-btn:hover {
  background: var(--ods-accent);
}

/* Request Form */
.request-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: var(--ods-radius);
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.info-box p {
  font-size: 14px;
  line-height: 1.5;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  font-size: 14px;
}

.form-input {
  padding: 12px 16px;
  border-radius: var(--ods-radius);
  border: 2px solid rgba(128, 128, 128, 0.3);
  background: transparent;
  font-size: 14px;
  transition: all 0.2s;
  color: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #10b981;
}

.form-input.error {
  border-color: #ef4444;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
}

.submit-btn {
  padding: 14px 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: var(--ods-ink);
  border: none;
  border-radius: var(--ods-radius);
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Help Section */
.help-section {
  margin-top: 24px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.1);
  border-radius: var(--ods-radius);
}

.help-section h4 {
  color: inherit;
  opacity: 0.9;
}
</style>
